import React, { useEffect, useState } from 'react';
import styles from './NewsUpdatesV2.module.scss';
import { getCompanyLogo, getFaviconUrl, UrlMetadataMap } from '../../../../../../utils/urlMetadata';
import AIGeneratedBadge from '../../../../../../common/AIGeneratedBadge/AIGeneratedBadge';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox';

interface News {
    imageUrl: string;
    redirectUrl: string;
    publishedDate: string;
    publisherName: string;
    title: string;
    snippet: string;
    newsSentiment: 'Positive' | 'Negative' | 'Neutral';
}

interface NewsReport {
    newsSummary: string;
    news: News[];
}

interface NewsUpdatesV2Props {
    newsReport: NewsReport;
}

// Define a type for tracking image loading status
interface ImageStatus {
    mainImageFailed: boolean;
    faviconFailed: boolean;
}

const NewsUpdatesV2: React.FC<NewsUpdatesV2Props> = ({ newsReport }) => {
    const [websiteMetadata, setWebsiteMetadata] = useState<UrlMetadataMap>({});
    const [imageStatus, setImageStatus] = useState<Record<string, ImageStatus>>({});

    // Return early if newsReport or news array is missing
    if (!newsReport || !newsReport.news || !Array.isArray(newsReport.news) || newsReport.news.length === 0) {
        return null;
    }

    useEffect(() => {
        const metadataMap: UrlMetadataMap = {};

        // Ensure news array exists and is an array before iterating
        if (newsReport.news && Array.isArray(newsReport.news)) {
            newsReport.news.forEach((news) => {
                if (news && news.redirectUrl) {
                    metadataMap[news.redirectUrl] = {
                        image: getCompanyLogo(news.redirectUrl),
                        favicon: getFaviconUrl(news.redirectUrl),
                    };
                }
            });
        }

        setWebsiteMetadata(metadataMap);

        // Reset image status when news report changes
        setImageStatus({});
    }, [newsReport]);

    // Handle main image load error
    const handleMainImageError = (newsIndex: number, redirectUrl: string) => {
        setImageStatus((prevStatus) => ({
            ...prevStatus,
            [newsIndex]: {
                ...prevStatus[newsIndex],
                mainImageFailed: true,
            },
        }));
    };

    // Handle favicon load error
    const handleFaviconError = (newsIndex: number) => {
        setImageStatus((prevStatus) => ({
            ...prevStatus,
            [newsIndex]: {
                ...prevStatus[newsIndex],
                faviconFailed: true,
            },
        }));
    };

    // Function to render the appropriate image or placeholder
    const renderPublisherImage = (newsItem: News, index: number) => {
        const status = imageStatus[index] || { mainImageFailed: false, faviconFailed: false };

        // If no redirectUrl or metadata, show placeholder immediately
        if (!newsItem?.redirectUrl || !websiteMetadata[newsItem.redirectUrl]) {
            return <div className={styles.placeholderImg}>{newsItem?.publisherName?.charAt(0)?.toUpperCase() || 'N'}</div>;
        }

        // If main image failed but we haven't tried favicon yet
        if (status.mainImageFailed && !status.faviconFailed && websiteMetadata[newsItem.redirectUrl]?.favicon) {
            return (
                <img
                    src={websiteMetadata[newsItem.redirectUrl].favicon}
                    alt={newsItem?.publisherName || 'Publisher'}
                    className={styles.publisherImage}
                    onError={() => handleFaviconError(index)}
                />
            );
        }

        // If both main image and favicon failed, show placeholder
        if (status.mainImageFailed && status.faviconFailed) {
            return <div className={styles.placeholderImg}>{newsItem?.publisherName?.charAt(0)?.toUpperCase() || 'N'}</div>;
        }

        // Default: try main image first
        return (
            <img
                src={websiteMetadata[newsItem.redirectUrl]?.image || websiteMetadata[newsItem.redirectUrl]?.favicon}
                alt={newsItem?.publisherName || 'Publisher'}
                className={styles.publisherImage}
                onError={() => handleMainImageError(index, newsItem.redirectUrl)}
            />
        );
    };

    const renderNewsContent = () => (
        <>
            {/* Render summary only if it exists */}
            {newsReport.newsSummary && (
                <div className={styles.summary}>
                    <p>{newsReport.newsSummary}</p>
                </div>
            )}

            <div className={styles.newsReportSection}>
                {newsReport.news.map((newsItem, index) => (
                    <div key={`news-${index}`} className={styles.newsReportContainer}>
                        <div className={styles.newsCard}>
                            <div className={styles.newsContent}>
                                <div className={styles.newsHeader}>
                                    <div className={styles.publisherImageContainer}>{renderPublisherImage(newsItem, index)}</div>
                                    <div className={styles.publisherInfo}>
                                        {newsItem?.publisherName && <span className={styles.publisherName}>{newsItem.publisherName}</span>}
                                        {newsItem?.redirectUrl && newsItem?.title && (
                                            <a
                                                href={newsItem.redirectUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.newsTitle}
                                            >
                                                {newsItem.publisherName ? '|  ' : ''}
                                                <span className={styles.newsTitleText}>{newsItem.title}</span>
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.newsFooter}>
                                    {newsItem?.newsSentiment && (
                                        <div className={`${styles.sentiment} ${styles[newsItem.newsSentiment.toLowerCase()] || ''}`}>
                                            {newsItem.newsSentiment}
                                        </div>
                                    )}
                                    {newsItem?.publishedDate && <div className={styles.publishDate}>{newsItem.publishedDate}</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );

    return (
        <HeaderBodyBox title="News & Updates" headerRight={<AIGeneratedBadge />}>
            {renderNewsContent()}
        </HeaderBodyBox>
    );
};

export default NewsUpdatesV2;
